import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";

import { cn } from "helpers/common";
import { buttonVariants } from "components/placement/Button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

/**
 * Renders a calendar component.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the calendar component.
 * @param {Object} [props.classNames] - Additional CSS class names for specific elements of the calendar.
 * @param {boolean} [props.showOutsideDays=true] - Determines whether to show days outside the current month.
 * @returns {JSX.Element} The rendered calendar component.
 *
 * @example
 * return (
 *  <Calendar
 *    mode="single"
 *    selected={date}
 *    onSelect={setDate}
 *    className="rounded-md border"
 *  />
 * )
 */
const Calendar: React.FC<CalendarProps> = ({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}) => {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months:
          "flex flex-col tablet:flex-row space-y-4 tablet:space-x-4 tablet:space-y-0",
        month: "space-y-4",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "text" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell: "text-gray-600 rounded-md w-9 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-primary-lightest [&:has([aria-selected])]:bg-primary-lighter first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day: cn(
          buttonVariants({ variant: "text" }),
          "h-9 w-9 p-0 font-normal aria-selected:opacity-100"
        ),
        day_range_end: "day-range-end",
        day_selected:
          "bg-primary text-white rounded-md hover:bg-primary hover:text-white focus:bg-primary focus:text-white",
        day_today: "border border-primary rounded-md text-primary",
        day_outside:
          "day-outside text-muted opacity-50 aria-selected:bg-primary-lightest aria-selected:text-primary-lighter",
        day_disabled: "text-gray-600 opacity-50",
        day_range_middle:
          "aria-selected:bg-primary-lighter aria-selected:text-primary",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ ...props }) => <ChevronLeft className="h-4 w-4" />,
        IconRight: ({ ...props }) => <ChevronRight className="h-4 w-4" />,
      }}
      {...props}
    />
  );
};
Calendar.displayName = "Calendar";

export { Calendar as default };
